export { IndexBinder }

/**
 * IndexBinder for loading layer-metadata from the search index.
 *
 * @author rhess <robin.hess@awi.de>
 * 
 * @memberof vef.map.importer.binder
 *
 */
class IndexBinder {

    constructor(url, indices) {
        this.url = url || "https://marine-data.de/rest/search/"
        this.indices = indices || ["service2"];
    }

    /**
     * Temporary method to fix issues from indexing
     * FixMe: Fix mapping errors in the index to directly create the resulting object
     * 
     * @private
     * @param {object} record
     * @returns {object} record
     */
    static fixRecord_(record) {
        try {
            if (typeof record.metadata.styles == "string") {
                record.metadata.styles = JSON.parse(decodeURIComponent(record.metadata.styles));
            }
            if (typeof record.metadata.attributes == "string") {
                record.metadata.attributes = JSON.parse(decodeURIComponent(record.metadata.attributes));
            }
            if (typeof record.metadata.dimensions == "string") {
                record.metadata.dimensions = JSON.parse(decodeURIComponent(record.metadata.dimensions));
            }
            if ("galleryMapping" in record.metadata) {
                record.metadata.mapping = {
                    "gallery": decodeURIComponent(record.metadata.galleryMapping)
                };
                delete record.metadata.galleryMapping;
            }
            if ("popupTemplate" in record.metadata) {
                if (!("metadataTemplates" in record.metadata)) record.metadata.metadataTemplates = {};
                record.metadata.metadataTemplates.popup = decodeURIComponent(record.metadata.popupTemplate)
                delete record.metadata.popupTemplate;
            }
            if ("sidebarTemplate" in record.metadata) {
                if (!("metadataTemplates" in record.metadata)) record.metadata.metadataTemplates = {};
                record.metadata.metadataTemplates.sidebar = decodeURIComponent(record.metadata.sidebarTemplate)
                delete record.metadata.sidebarTemplate;
            }
        } catch (e) { console.warn(e) }
        return record;
    }

    fetch(params) {
        return new Promise((resolve, reject) => {
            const content = JSON.stringify(Object.assign({
                indices: this.indices,
                types: [],
                sorts: [],
                facets: [],
                excludes: [],
                includes: [],
                queryFacets: {},
                queryGeometries: {},
                offset: 0,
                hits: 0,
                query: ""
            }, params || {}));

            const xhr = new XMLHttpRequest();
            xhr.onreadystatechange = () => {
                if (xhr.readyState == 4) {
                    if (xhr.status == 200) {
                        const json = JSON.parse(xhr.responseText)
                        json.records.forEach(record => IndexBinder.fixRecord_(record));
                        resolve(json);
                    } else {
                        reject();
                    }
                }
            };
            xhr.open("POST", this.url, true);
            xhr.setRequestHeader('Accept', 'application/json;charset=UTF-8');
            xhr.setRequestHeader('content-type', 'application/json;charset=UTF-8');
            xhr.send(content);
        });
    }

    /**
     * Get layers from the catalog queried by the given options
     *
     * @param {string[]} ids any amount of ids
     * @returns {Promise} promise returns an array of layers in the index format
     */
    fetchLayersByIds(ids) {
        return new Promise((resolve, reject) => {
            let query = '(uniqueId:(';
            for (let i = 0; i < ids.length; ++i) {
                query += '"' + ids[i] + '" ';
            }
            query += '))';

            this.fetch({ query: query, hits: ids.length })
                .then(response => {
                    const layers = {};
                    for (let i = 0; i < response.records.length; ++i) {
                        const layer = IndexBinder.createLayer(response.records[i]);
                        if (layer) layers[response.records[i].uniqueId] = layer;
                    }
                    resolve(layers);
                })
                .catch(() => reject())
        });
    }

    static geoJsonToBounds_(record) {
        const bounds = {}
        if (record.geometry && (record.geometry.type.toLowerCase() == "polygon") && record.geometry.coordinates.length > 0) {
            record.geometry.coordinates[0].forEach(LngLat => {
                if (isNaN(bounds.minX) || (LngLat[0] < bounds.minX)) bounds.minX = LngLat[0];
                if (isNaN(bounds.maxX) || (LngLat[0] > bounds.maxX)) bounds.maxX = LngLat[0];
                if (isNaN(bounds.minY) || (LngLat[1] < bounds.minY)) bounds.minY = LngLat[1];
                if (isNaN(bounds.maxY) || (LngLat[1] > bounds.maxY)) bounds.maxY = LngLat[1];
            });
        }
        return bounds;
    }

    /**
     * Create an object that uses the VEF internal layer structure from the catalog metadata
     *
     * @param {object} record metadata from catalog for a layer
     * @returns {object} object containing formatted layer metadata
     */
    static createLayer(record) {
        const config = {};

        for (let i = 0; i < record.resources.length; ++i) {
            const resource = record.resources[i];
            if (resource.type.toLowerCase() == "service") {
                if (resource.title) config.serviceTitle = resource.title;
                if (resource.uri) config.serviceUrl = resource.uri;
                if (resource.metadata) {
                    const metadata = resource.metadata;
                    if (metadata.type) config.type = metadata.type.toLowerCase();
                    if (metadata.name) config.serviceName = metadata.name;
                    if (metadata.accessConstraints) config.serviceAccessConstraints = metadata.accessConstraints;
                    if (metadata.fees) config.serviceFees = metadata.fees;
                    if (metadata.abstract) config.serviceAbstract = metadata.abstract;
                    if (metadata.software) config.serviceSoftware = metadata.software;
                    if (metadata.version) config.serviceVersion = metadata.version;
                    if (metadata.filterType) config.filterType = metadata.filterType;
                    if (metadata.keywords) config.serviceKeywords = metadata.keywords;
                    if (metadata.crs) config.availableCrs = metadata.crs;
                }
                break;
            }
        }

        if (!config.type) return null;

        if (record.title) {
            config.title = record.title;
            config.originalTitle = record.title;
        }
        if (record.description) config.abstract = record.description;
        if (record.uniqueId) config.catalogId = record.uniqueId;

        if (record.metadata) {
            const metadata = record.metadata;
            if (metadata.attributes) config.attributeFields = metadata.attributes;
            if (metadata.crs) config.availableCrs = ("availableCrs" in config) ? config.availableCrs.concat(metadata.crs) : metadata.crs;
            if (metadata.name) config.name = metadata.name;
            if (metadata.styles) config.availableStyles = metadata.styles;
            if (metadata.dimensions) config.dimensions = metadata.dimensions;
            if (metadata.keywords) config.keywords = metadata.keywords;
            if (metadata.metadataTemplates) config.metadataTemplate = metadata.metadataTemplates;
            if (metadata.mapping) config.mapping = metadata.mapping;
        }

        Object.assign(config, this.geoJsonToBounds_(record));

        return config;
    }

}